import type { CSSProperties, ReactNode } from 'react';
import { usePopup } from '@/hooks/usePopup';

/**
 * Shell de modal do TARP (overlay + card). Fecha no ×, clicando fora ou no Esc
 * (C-91: o Esc chama o mesmo `onClose` do × — se ele pede confirmação, o Esc também).
 * Header fiel ao `renderTarpModal` do protótipo: quando recebe `accent` (cor
 * do nível), pinta o contorno do card e o header (borda + fundo tingido) e
 * colore o título; `subtitle` e `headerRight` (chip de status) completam o topo.
 */
export function Modal({
  title,
  subtitle,
  accent,
  headerRight,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  /** Cor do nível (hex). Pinta contorno/header e colore o título. */
  accent?: string;
  /** Slot à direita do header — ex.: chip de status. */
  headerRight?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = usePopup<HTMLDivElement>({ aoFechar: onClose });
  const boxStyle: CSSProperties | undefined = accent ? { borderColor: accent } : undefined;
  // Header OPACO: mantém o fundo sólido (bg-jf-surface na classe) e aplica o
  // tom do nível como CAMADA (background-image) por cima — senão o accent
  // translúcido substituiria o fundo e o header sticky deixaria o conteúdo
  // rolar visível atrás (a "transparência esquisita").
  const headerStyle: CSSProperties | undefined = accent
    ? { borderBottomColor: accent, backgroundImage: `linear-gradient(${accent}1f, ${accent}1f)` }
    : undefined;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 md:items-center"
      onClick={onClose}
    >
      {/* Card = coluna flex com overflow-hidden: o clip respeita as bordas
          arredondadas e a rolagem fica SÓ no corpo (a barra não corre por cima
          do header/footer fixos nem sobre os cantos — fiel ao .modal do protótipo). */}
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="flex max-h-[90dvh] focus:outline-none w-full flex-col overflow-hidden rounded-t-2xl border-2 border-jf-border bg-jf-surface md:max-w-lg md:rounded-2xl"
        style={boxStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-jf-border bg-jf-surface p-4" style={headerStyle}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2
                className={`text-base font-black leading-tight ${accent ? '' : 'text-jf-yellow'}`}
                style={accent ? { color: accent } : undefined}
              >
                {title}
              </h2>
              {subtitle && <p className="mt-1 text-xs text-jf-muted">{subtitle}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {headerRight}
              <button
                type="button"
                onClick={onClose}
                // GUPM-293: "Fechar janela" e não "Fechar" — o rodapé passou a ter um
                // botão "Fechar", e dois controles com o mesmo nome acessível deixam
                // ambíguo para leitor de tela qual deles é o × do canto.
                aria-label="Fechar janela"
                className="text-lg leading-none text-jf-muted hover:text-white"
              >
                ×
              </button>
            </div>
          </div>
        </div>
        {/* Único container rolável (min-h-0 permite encolher no flex). */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-jf-border bg-jf-surface p-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

/** Classe compartilhada dos selects/inputs dos modais do TARP. */
export const FIELD_CLS =
  'w-full rounded-lg border border-jf-border bg-jf-surface2 px-3 py-2 text-sm text-white ' +
  'focus:border-jf-yellow focus:outline-none disabled:opacity-50';
